import ActivityChipTableCell from 'Components/ActivityChipTableCell/ActivityChipTableCell'
import ClickableTableCell from 'Components/ClickableTableCell'
import CustomButton from 'Components/CustomButton'
import CustomTablePagination from 'Components/CustomTablePagination/CustomTablePagination'
import type { HeadCell } from 'Components/EnhancedTableHead/EnhancedTableHead'
import EnhancedTableHead from 'Components/EnhancedTableHead/EnhancedTableHead'
import StyledTableRow from 'Components/StyledTableRow'
import { WithLoader } from 'Components/WithLoader'
import { SELECTED_SALES_POINT_PARAMETER_KEY } from 'Services/ServiceProviderManagement/Features/SalesPoint/Hooks/useSalesPointsWithParams'
import { useGetSalesPointsQuery } from 'Services/ServiceProviderManagement/Features/SalesPoint/salesPointApi'
import type { FetchSalesPointListParams } from 'Services/ServiceProviderManagement/Features/SalesPoint/types'
import { SELECTED_SERVICE_PROVIDER_PARAMETER_KEY } from 'Services/ServiceProviderManagement/Features/ServiceProvider/Hooks/useServiceProvidersWithParams'
import type { ServiceProvider } from 'Services/ServiceProviderManagement/Features/ServiceProvider/types'
import {
  ROWS_PER_PAGE_DEFAULT,
  SERVICE_PROVIDER_MANAGEMENT_ROOT_URL,
  SORT_ORDER_ASC
} from 'Util/constants'
import { showValueOrHyphen } from 'Util/formatters'
import useListFunctions from 'Util/hooks/useListFunctions'
import AddIcon from '@mui/icons-material/Add'
import { Box, Paper, Table, TableBody, TableCell, TableContainer, Typography } from '@mui/material'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { t } from 'ttag'

interface ServiceProviderSalesPointsProps {
  serviceProvider: ServiceProvider
}

export function ServiceProviderSalesPoints({
  serviceProvider
}: Readonly<ServiceProviderSalesPointsProps>) {
  const baseUrl = `${SERVICE_PROVIDER_MANAGEMENT_ROOT_URL}/sales-points`
  const navigate = useNavigate()

  const headCells: HeadCell[] = [
    {
      id: 'salesPointId',
      numeric: false,
      disablePadding: false,
      label: t`Code`,
      sortable: true
    },
    {
      id: 'name',
      numeric: false,
      disablePadding: false,
      label: t`Sales point name`,
      sortable: true
    },
    {
      id: 'activity',
      numeric: false,
      disablePadding: false,
      label: t`Activity`,
      sortable: false
    }
  ]

  /**
   * Query params are separated from searchParams
   * to avoid affecting service provider list page state.
   */
  const [searchQueryParams, setSearchQueryParams] = useState<Partial<FetchSalesPointListParams>>({
    start_index: undefined,
    count: ROWS_PER_PAGE_DEFAULT,
    sort_by: 'salesPointId',
    sort_order: SORT_ORDER_ASC
  })

  const { handleChangePage, handleRequestSort } = useListFunctions({
    searchQueryParams,
    setSearchQueryParams
  })

  const {
    data: { resources: salesPoints } = { resources: [] },
    data: { total_results: totalResults } = { total_results: 0 },
    isFetching: isServiceProvidersSalesPointsFetching
  } = useGetSalesPointsQuery(
    {
      ...searchQueryParams,
      service_provider_id: serviceProvider.service_provider_id
    },
    { skip: !serviceProvider }
  )

  const orderPrefix = `${searchQueryParams.sort_order === SORT_ORDER_ASC ? '-' : ''}`
  const orderBy = `${orderPrefix}${searchQueryParams.sort_by}`

  const navigateToSalesPointPage = (salesPointId: string) => {
    return navigate(`${baseUrl}/?${SELECTED_SALES_POINT_PARAMETER_KEY}=${salesPointId}`)
  }

  return (
    <Box>
      <WithLoader loading={isServiceProvidersSalesPointsFetching}>
        <Paper sx={{ width: '100%', mb: 2, pt: 2 }}>
          <TableContainer>
            <Table aria-labelledby="tableTitle" size="small">
              <EnhancedTableHead
                orderBy={orderBy}
                headCells={headCells}
                onRequestSort={handleRequestSort}
              />
              <TableBody>
                {salesPoints.length > 0 ? (
                  salesPoints.map(row => {
                    return (
                      <StyledTableRow hover tabIndex={-1} key={row.sales_point_id}>
                        <ClickableTableCell
                          onClick={() => navigateToSalesPointPage(row.sales_point_id)}
                        >
                          {showValueOrHyphen(row.sales_point_id)}
                        </ClickableTableCell>
                        <ClickableTableCell
                          onClick={() => navigateToSalesPointPage(row.sales_point_id)}
                        >
                          {showValueOrHyphen(row.name)}
                        </ClickableTableCell>
                        <ActivityChipTableCell active={row.active} />
                      </StyledTableRow>
                    )
                  })
                ) : (
                  <StyledTableRow>
                    <TableCell colSpan={3}>
                      <Typography variant="body2">{t`Service provider has no sales points.`}</Typography>
                    </TableCell>
                  </StyledTableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <CustomTablePagination
            count={totalResults}
            page={
              searchQueryParams.start_index
                ? Number.parseInt(searchQueryParams.start_index, 10) / ROWS_PER_PAGE_DEFAULT
                : 0
            }
            onPageChange={handleChangePage}
          />
        </Paper>
        <CustomButton
          variant="text"
          size="small"
          startIcon={<AddIcon />}
          sx={{ ml: 2, mb: 2 }}
          onClick={() =>
            navigate(
              `${baseUrl}/create?${SELECTED_SERVICE_PROVIDER_PARAMETER_KEY}=${serviceProvider.service_provider_id}`
            )
          }
        >{t`Add sales point`}</CustomButton>
      </WithLoader>
    </Box>
  )
}
